import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UIEvent } from "react";
import { useLazyGetBackendUsersQuery, type GetAdminUsersParams } from "features/adminUsers/adminUsers";
import type { BackendUser } from "types/interfaces/adminUsers";

const SCROLL_THRESHOLD_PX = 48;

function appendDedupe(prev: BackendUser[], more: BackendUser[]): BackendUser[] {
  if (!more.length) return prev;
  const ids = new Set(prev.map((u) => u.id));
  const extra = more.filter((u) => !ids.has(u.id));
  return extra.length ? [...prev, ...extra] : prev;
}

export interface UseBackendUsersAutocompleteInfiniteOptions {
  skip?: boolean;
  page_size?: number;
  /** Raw input; debounced inside the hook for API calls. */
  search: string;
  sort_by?: NonNullable<GetAdminUsersParams["sort_by"]>;
  sort_order?: GetAdminUsersParams["sort_order"];
  debounceMs?: number;
  /** Merged into `options` when not already in the loaded list (current selection from API, etc.). */
  value?: BackendUser | null;
}

export function useBackendUsersAutocompleteInfinite({
  skip = false,
  page_size = 50,
  search,
  sort_by = "name",
  sort_order = "asc",
  debounceMs = 300,
  value = null,
}: UseBackendUsersAutocompleteInfiniteOptions) {
  const [debouncedSearch, setDebouncedSearch] = useState(() => search.trim());
  const [rows, setRows] = useState<BackendUser[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [trigger, { isFetching }] = useLazyGetBackendUsersQuery();
  const sessionIdRef = useRef(0);
  const nextPageToFetchRef = useRef(2);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchDebounceBootstrapped = useRef(false);

  useEffect(() => {
    if (!searchDebounceBootstrapped.current) {
      searchDebounceBootstrapped.current = true;
      setDebouncedSearch(search.trim());
      return;
    }
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      setDebouncedSearch(search.trim());
    }, debounceMs);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [search, debounceMs]);

  useEffect(() => {
    if (skip) {
      setRows([]);
      setHasMore(false);
      return;
    }
    const sid = ++sessionIdRef.current;
    setRows([]);
    setHasMore(false);
    nextPageToFetchRef.current = 2;
    void trigger({
      page: 1,
      page_size,
      search: debouncedSearch || undefined,
      sort_by,
      sort_order,
    })
      .unwrap()
      .then((res) => {
        if (sid !== sessionIdRef.current) return;
        setRows(res.results);
        setHasMore(Boolean(res.next));
        nextPageToFetchRef.current = res.next ? 2 : 1;
      })
      .catch(() => {
        if (sid !== sessionIdRef.current) return;
        setRows([]);
        setHasMore(false);
        nextPageToFetchRef.current = 2;
      });
  }, [skip, debouncedSearch, page_size, sort_by, sort_order, trigger]);

  const loadMore = useCallback(() => {
    if (skip || !hasMore || isFetching) return;
    const sid = sessionIdRef.current;
    const page = nextPageToFetchRef.current;
    if (page < 2) return;
    void trigger({
      page,
      page_size,
      search: debouncedSearch || undefined,
      sort_by,
      sort_order,
    })
      .unwrap()
      .then((res) => {
        if (sid !== sessionIdRef.current) return;
        setRows((prev) => appendDedupe(prev, res.results));
        setHasMore(Boolean(res.next));
        nextPageToFetchRef.current = res.next ? page + 1 : page;
      })
      .catch(() => {
        if (sid !== sessionIdRef.current) return;
      });
  }, [
    skip,
    hasMore,
    isFetching,
    debouncedSearch,
    page_size,
    sort_by,
    sort_order,
    trigger,
  ]);

  const onListboxScroll = useCallback(
    (e: UIEvent<HTMLUListElement>) => {
      const t = e.currentTarget;
      if (t.scrollTop + t.clientHeight < t.scrollHeight - SCROLL_THRESHOLD_PX) return;
      loadMore();
    },
    [loadMore],
  );

  const options = useMemo(() => {
    if (!value) return rows;
    if (rows.some((u) => u.id === value.id)) return rows;
    return [value, ...rows];
  }, [rows, value]);

  const loading = isFetching && rows.length === 0;

  return {
    options,
    loading,
    isFetching,
    onListboxScroll,
  };
}
